import React, { Fragment, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchEnquiryById } from "slices/enquiry/thunk";
import { toast } from "react-toastify";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { tableStyles } from "Constants/Style";
import FilePreviewPopover from "components/FilePreview/FilePreviewPopover";
import PageContainer from "components/PageContainer/PageContainer";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import CancelBackButton from "components/Buttons/CancelBackButton";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";

const SiteVisitDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  // Redux selector for enquiry state
  const selectEnquiryData = createSelector(
    (state: any) => state.Enquiry,
    (enquiry) => ({
      selectedEnquiry: enquiry.selectedEnquiry,
      loading: enquiry.loading,
      error: enquiry.error,
    })
  );

  const { selectedEnquiry, loading, error } = useSelector(selectEnquiryData);

  // Fetch enquiry data using Redux thunk
  useEffect(() => {
    if (!id) {
      toast.error("Site visit ID is missing");
      navigate("/sitevisit");
      return;
    }

    // Dispatch thunk to fetch enquiry by ID
    dispatch(fetchEnquiryById(id));
  }, [id, dispatch, navigate]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      navigate("/sitevisit");
    }
  }, [error, navigate]);

  const handleCancel = () => {
    navigate("/sitevisit");
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return "-";
    return new Date(date).toISOString().split("T")[0];
  };

  // if (!selectedEnquiry) {
  //   return (
  //     <div className="page-content px-4">
  //       <div className="text-center py-5 bg-white rounded-3 shadow-sm">
  //         <p>No site visit data found</p>
  //         <Button variant="primary" onClick={handleCancel}>
  //           Back to Site Visit List
  //         </Button>
  //       </div>
  //     </div>
  //   );
  // }

  const enquiryData = selectedEnquiry;

  return (
    <PageContainer>
      <Card.Header>
        <FormHeader
          title="Site Visit Information"
          firstLabel="Site Visit"
          secondLabel="View Details"
          handleArrowClick={handleCancel}
          handleBreadcrumbClick={handleCancel}
        />
      </Card.Header>

      <hr />

      {loading ? (
        <LoadingSpinner />
      ) : (
        <Card.Body>
          {/* Enquiry Details */}
          <Row className="mb-3">
            <p>
              <h6 className="card-title mb-0 mt-3">Enquiry Details</h6>
            </p>
            <Col md={6}>
              <p>
                <strong>Enquiry No. :</strong>&nbsp;
                {enquiryData?.enquiry_number || "—"}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Enquiry Date :</strong>&nbsp;
                {formatDate(enquiryData?.enquiry_date)}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Source of Enquiry :</strong>&nbsp;
                {enquiryData?.enquiry_source || "—"}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Remark :</strong>&nbsp;
                {enquiryData?.remark || "—"}
              </p>
            </Col>
          </Row>

          <hr />

          {/* Customer Details */}
          <Row className="mb-3">
            <p>
              <h6 className="card-title mb-0 mt-3">Customer Details</h6>
            </p>
            <Col md={6}>
              <p>
                <strong>Customer Name :</strong>&nbsp;
                {enquiryData?.customer_details?.customer_name || "—"}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Contact Person Name :</strong>&nbsp;
                {enquiryData?.customer_details?.contact_person_name || "—"}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Contact Person Phone No. :</strong>&nbsp;
                {enquiryData?.customer_details?.contact_person_phone_no || "—"}
              </p>
            </Col>
          </Row>

          <hr />

          {/* Site Visit Details */}
          {enquiryData?.site_visit_required && enquiryData?.site_visit && (
            <>
              <Row className="mb-3">
                <p>
                  <h6 className="card-title mb-0 mt-3">Site Visit Details</h6>
                </p>
                <Col md={6}>
                  <p>
                    <strong>Site Visit Date :</strong>&nbsp;
                    {formatDate(enquiryData?.site_visit?.site_visit_date)}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Staff Name :</strong>&nbsp;
                    {enquiryData?.site_visit?.staff_name || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Location URL :</strong>&nbsp;
                    {enquiryData?.site_visit?.location_url ? (
                      <a
                        href={enquiryData.site_visit.location_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: "underline",
                          color: "#0033aa", // dark blue
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                        }}
                      >
                        {enquiryData.site_visit.location_url
                          ? "Visit location"
                          : "-"}
                      </a>
                    ) : (
                      "—"
                    )}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Address :</strong>&nbsp;
                    {enquiryData?.site_visit?.address || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>City :</strong>&nbsp;
                    {enquiryData?.site_visit?.city || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>State :</strong>&nbsp;
                    {enquiryData?.site_visit?.state || "—"}
                  </p>
                </Col>
                <Col md={12}>
                  <p>
                    <strong>Remarks :</strong>&nbsp;
                    {enquiryData?.site_visit?.remarks || "—"}
                  </p>
                </Col>
              </Row>
              <hr />
            </>
          )}

          {/* Item Details */}
          {enquiryData?.enquiry_items &&
            enquiryData.enquiry_items.length > 0 && (
              <>
                <Row className="mb-3">
                  <p>
                    <h6 className="card-title mb-0 mt-3">Item Details</h6>
                  </p>
                  <Col md={12}>
                    <style>
                      {`
                          .sticky-column {
                            position: relative;
                          }
                          .sticky-column::before {
                            content: '';
                            position: absolute;
                            left: 0;
                            top: 0;
                            bottom: 0;
                            width: 1px;
                            background-color: #dee2e6;
                            z-index: 12;
                          }
                          thead .sticky-column::before {
                            z-index: 13;
                          }
                          .remark-cell {
                            max-width: 300px;
                            white-space: normal !important;
                            word-wrap: break-word;
                            overflow-wrap: break-word;
                          }
                        `}
                    </style>
                    <div className="mb-3 mt-3" style={{ overflowX: "auto" }}>
                      <Table
                        hover
                        responsive
                        className="align-middle text-center"
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "70px",
                              }}
                            >
                              Sr No.
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "150px",
                              }}
                            >
                              Category
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "150px",
                              }}
                            >
                              Sub Category
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "150px",
                              }}
                            >
                              Item Name
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "120px",
                              }}
                            >
                              Item Code
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                              }}
                            >
                              Unit
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                              }}
                            >
                              Length
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                              }}
                            >
                              Width
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "100px",
                              }}
                            >
                              Height
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "150px",
                              }}
                            >
                              Required Quantity
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "150px",
                              }}
                            >
                              File
                            </th>
                            <th
                              style={{
                                ...tableStyles.headerAlt,
                                minWidth: "200px",
                              }}
                            >
                              Remarks
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {enquiryData.enquiry_items.map(
                            (item: any, index: number) => (
                              <tr key={item.enquiry_item_id || index}>
                                <td style={tableStyles.cell}>{index + 1}</td>
                                <td style={tableStyles.cell}>
                                  {item.category_name || "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  {item.sub_category_name || "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  {item.item_name || "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  {item.item_code || "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  {item.unit?.unit_name ||
                                    item.unit_name ||
                                    "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  {item.length || "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  {item.width || "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  {item.height || "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  {item.required_quantity < 0
                                    ? Math.abs(item.required_quantity)
                                    : item.required_quantity || "-"}
                                </td>
                                <td style={tableStyles.cell}>
                                  <FilePreviewPopover
                                    fileUrl={item.item_file}
                                    index={index}
                                  />
                                </td>
                                <td
                                  className="remark-cell"
                                  style={tableStyles.cell}
                                >
                                  <TruncatedTextCell
                                    text={item.remarks}
                                    maxWidth="300px"
                                    maxLines={4}
                                    rowIndex={index}
                                    fallbackText="-"
                                  />
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>
                <hr />
              </>
            )}

          <div className="d-flex flex-wrap justify-content-center gap-3 mt-5">
            <CancelBackButton label="Back" onClick={handleCancel} />
          </div>
        </Card.Body>
      )}
    </PageContainer>
  );
};

export default SiteVisitDetailsPage;
